import { getSubunitMultiplier } from '@bbpos/utils';
import type { CurrencyCode } from '@bbpos/types';
import type { ModifierStats } from '@/services/modifier.service';

export interface ModifierKpis {
  totalGroups: number;
  requiredGroups: number;
  avgOptions: number;
}

// Regression: GitHub issue #285 (Part A) — "Modifiers 'Avg Options' KPI is
// page-scoped".
//
// The KPI cards must reflect EVERY modifier group (matching the current filters)
// and stay constant across pages. They now come from the tenant-/location-wide
// `/modifiers/stats` aggregate, never from the current 20-row page slice. This
// maps that stats response into the KPI values; before it loads (null) we show
// zeros rather than a page-derived guess. `avgOptions` is rounded to one decimal
// to match the original display.
export function deriveModifierKpis(stats: ModifierStats | null): ModifierKpis {
  return {
    totalGroups: stats?.total_groups ?? 0,
    requiredGroups: stats?.required_groups ?? 0,
    avgOptions: stats ? Math.round(stats.avg_options * 10) / 10 : 0,
  };
}

// Regression: GitHub issue #285 (Part B) — the modifier price-adjustment form
// hardcoded a ÷100/×100 conversion that assumes 2-decimal currencies, silently
// 10×-under-recording adjustments for 3-decimal currencies (e.g. OMR/BHD/KWD).
//
// These derive the subunit divisor/multiplier from the tenant's currency instead
// (INR: 100, OMR: 1000), so the stored minor-unit amount round-trips correctly.

// Minor unit (paise/baisa) → the string shown in the price input.
export function priceMinorToDisplay(minor: number, currency: CurrencyCode): string {
  return String(minor / getSubunitMultiplier(currency));
}

// Price input string → integer minor unit (paise/baisa) for persistence.
export function priceDisplayToMinor(display: string, currency: CurrencyCode): number {
  return Math.round(parseFloat(display || '0') * getSubunitMultiplier(currency));
}
